/**
 * Paddle Success Page Component
 * Handles successful payment redirects from Paddle
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useSubscription } from '../../context/SubscriptionContext';
import {
  getCheckoutReturnParams,
  getStoredCheckoutBillingCycle,
  CHECKOUT_SESSION_STORAGE_KEY,
  CHECKOUT_PLAN_STORAGE_KEY,
} from '../../utils/checkoutReturnParams';
import { slugToDisplayName } from '../../utils/planCatalogUtils';
import { isCheckoutAlreadyDone } from '../../utils/checkoutLock';
import { isMockPaymentsAllowed } from '../../config/appConfig';

const formatPaidAmount = (amount, billingCycle) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const suffix = billingCycle === 'yearly' ? '/yr' : '/mo';
  return `$${n.toFixed(2)}${suffix}`;
};

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [successData, setSuccessData] = useState(null);
  const [error, setError] = useState(null);
  const { completeCheckoutReturn, refreshData } = useSubscription();
  const verifyStartedRef = useRef(false);

  useEffect(() => {
    if (verifyStartedRef.current) return;
    verifyStartedRef.current = true;

    let cancelled = false;

    const runVerify = async () => {
      try {
        console.log('[DEBUG] SuccessPage runVerify started');
        console.log('[DEBUG] window.location.href:', window.location.href);
        console.log('[DEBUG] window.location.search:', window.location.search);
        console.log('[DEBUG] window.location.hash:', window.location.hash);

        const hashParams = getCheckoutReturnParams();
        console.log('[DEBUG] hashParams:', hashParams);

        const storedSessionId = sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_KEY);
        const storedPlanId = sessionStorage.getItem(CHECKOUT_PLAN_STORAGE_KEY);
        console.log('[DEBUG] sessionStorage values:', { storedSessionId, storedPlanId });

        // ***** BH29072026_1830 — track whether the id came from the URL (a real Paddle
        // return) or only from sessionStorage (written before the overlay even opened)
        const urlTransactionId =
          searchParams.get('transaction_id') ||
          searchParams.get('checkout_id') ||
          searchParams.get('session_id') ||
          searchParams.get('_ptxn') ||
          hashParams.checkoutId;

        let transactionId = urlTransactionId || storedSessionId;

        console.log('[DEBUG] parsed transactionId before placeholder cleanup:', transactionId);

        // Clean up template placeholders that were not replaced by the client SDK
        if (
          transactionId &&
          (transactionId === '{TRANSACTION_ID}' || transactionId === '{CHECKOUT_SESSION_ID}')
        ) {
          transactionId = searchParams.get('_ptxn') || null;
          console.log('[DEBUG] placeholder cleaned, fallback transactionId:', transactionId);
        }

        // ***** Code Start BH29072026_1105 - Mock txn ids only when mock payments allowed (Sathis)
        const isDevOrMockAllowed = isMockPaymentsAllowed();

        const isRealTxn =
          transactionId &&
          (transactionId.startsWith('txn_') ||
            transactionId.startsWith('che_') ||
            transactionId.startsWith('cs_') ||
            (isDevOrMockAllowed &&
              (transactionId.startsWith('mock_') || transactionId.startsWith('cs_mock_'))));
        // ***** Code End BH29072026_1105

        console.log('[DEBUG] isRealTxn:', isRealTxn);

        // ***** Code Start BH29072026_1830 - Only a real Paddle return implies success
        // A transaction id left in sessionStorage by an abandoned checkout used to satisfy
        // isRealTxn and make success 'true', which verified a draft transaction.
        // ***** Code Start BH30072026_1515 - Paddle default success URL is sparse
        // Overlay/dashboard redirects often land as /success?customer_email=... with no
        // success=true and no transaction_id. That is still a paid return when we also
        // have the che_/txn_ stored at checkout open; abandoned closes clear that storage.
        const customerEmailParam =
          searchParams.get('customer_email') || hashParams.customerEmail || null;
        const paddleReturnSignal = Boolean(
          urlTransactionId ||
            searchParams.get('success') === 'true' ||
            hashParams.success ||
            customerEmailParam
        );
        const success =
          searchParams.get('success') ||
          (hashParams.success
            ? 'true'
            : isRealTxn && (urlTransactionId || (paddleReturnSignal && storedSessionId))
              ? 'true'
              : null);
        // ***** Code End BH30072026_1515
        // ***** Code End BH29072026_1830

        console.log('[DEBUG] success value determined:', success);

        const plan = searchParams.get('plan') || hashParams.planId || storedPlanId;
        const billingCycle =
          searchParams.get('billing_cycle') ||
          searchParams.get('billing_interval') ||
          hashParams.billingCycle ||
          getStoredCheckoutBillingCycle() ||
          'monthly';

        console.log('[DEBUG] final params:', {
          success,
          transactionId,
          plan,
          billingCycle
        });

        if (success !== 'true' || !transactionId) {
          console.error('[DEBUG] validation failed: success is not "true" or transactionId is missing/empty');
          if (!cancelled) setError('Invalid success parameters');
          return;
        }

        const alreadyDone = isCheckoutAlreadyDone(transactionId);
        const checkoutResult = alreadyDone
          ? { processed: false, skipped: true, verifyResult: null }
          : await completeCheckoutReturn(transactionId, plan, billingCycle);

        if (cancelled) return;

        const verify = checkoutResult?.verifyResult || {};
        const planSlug = verify.plan_id || verify.plan || plan;
        const displayPlan =
          verify.plan_name ||
          slugToDisplayName(planSlug) ||
          slugToDisplayName(plan);
        const paidAmount =
          verify.price ??
          verify.amount ??
          verify.total ??
          null;
        const cycle = verify.billing_cycle || billingCycle;

        setSuccessData({
          plan: displayPlan,
          planSlug: verify.plan_id || plan,
          transactionId,
          customerEmail: verify.customer_email || null,
          amount: paidAmount,
          billingCycle: cycle,
          amountLabel: formatPaidAmount(paidAmount, cycle),
          status: verify.status || 'paid',
        });

        notifyDesktopApp('payment_success', {
          plan: displayPlan,
          transactionId,
          amount: paidAmount,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'An error occurred while processing your payment');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    runVerify();
    return () => {
      cancelled = true;
    };
    // Intentionally run once per mount — do not re-verify when subscription state updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notifyDesktopApp = (type, data) => {
    try {
      // Notify desktop app via postMessage
      if (window.parent !== window) {
        window.parent.postMessage({
          type: 'paddle_payment_success',
          data: data
        }, '*');
      }
      
      // Store in localStorage for desktop app
      localStorage.setItem('last_payment_success', JSON.stringify({
        timestamp: new Date().toISOString(),
        type: type,
        data: data
      }));
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('paddlePaymentSuccess', {
        detail: { type, data }
      }));
    } catch (error) {
      // Error notifying desktop app
    }
  };

  const handleContinue = () => {
    if (typeof refreshData === 'function') {
      refreshData();
    }
    navigate('/dashboard');
  };

  const handleViewSubscription = () => {
    if (typeof refreshData === 'function') {
      refreshData();
    }
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Verifying your payment...</p>
          <p className="text-gray-400 text-sm mt-2">This may take up to 15 seconds. Please wait.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircleIcon className="w-8 h-8 text-green-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
        <p className="text-gray-600 mb-6">
          Your subscription has been activated successfully.
        </p>
        
        {successData && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-2">Payment Details</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">Plan:</span> {successData.plan}</p>
              <p>
                <span className="font-medium">Amount:</span>{' '}
                {successData.amountLabel ||
                  (successData.amount != null && Number(successData.amount) > 0
                    ? `$${Number(successData.amount).toFixed(2)}`
                    : '—')}
              </p>
              {successData.billingCycle && (
                <p>
                  <span className="font-medium">Billing:</span>{' '}
                  {successData.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                </p>
              )}
              <p><span className="font-medium">Status:</span> {successData.status}</p>
              {successData.customerEmail && (
                <p><span className="font-medium">Email:</span> {successData.customerEmail}</p>
              )}
            </div>
          </div>
        )}
        
        <div className="space-y-3">
          <button
            onClick={handleViewSubscription}
            className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
          >
            View Subscription
            <ArrowRightIcon className="w-4 h-4 ml-2" />
          </button>
          
          <button
            onClick={handleContinue}
            className="w-full bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Continue to Dashboard
          </button>
        </div>
        
        <p className="text-xs text-gray-500 mt-4">
          You can now access all premium features in your desktop application.
        </p>
      </div>
    </div>
  );
};

export default SuccessPage;


